import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export default function HowItWorks() {
  const steps = [
    {
      step: "Step 1",
      title: "Analyse the Market",
      description: "Use your strategy to identify setups",
      testId: "how-it-works-step-1"
    },
    {
      step: "Step 2",
      title: "Update the Compass",
      description: "Log your trade plan and validate your decision",
      testId: "how-it-works-step-2"
    },
    {
      step: "Step 3",
      title: "Execute the Trades",
      description: "Trade with confidence and discipline",
      testId: "how-it-works-step-3"
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-white" data-testid="how-it-works-section">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <h2 
            className="text-3xl md:text-4xl font-semibold text-[#337AF7] tracking-tight"
            data-testid="how-it-works-title"
          >
            How It Works
          </h2>
        </motion.div>

        {/* 3 Steps with Arrows */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
          {steps.map((item, index) => {
            return (
              <React.Fragment key={index}>
                {/* Step Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                  data-testid={item.testId}
                  className="flex flex-col items-center text-center w-full md:w-64"
                >
                  {/* Step Number Circle */}
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#337AF7]/10 border-2 border-[#337AF7]/30 flex items-center justify-center mb-4 shadow-lg">
                    <span className="text-[#337AF7] font-semibold text-sm md:text-base">
                      {item.step}
                    </span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg md:text-xl font-semibold text-[#162959] mb-2">
                    {item.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-[#162959]/60">
                    {item.description}
                  </p>
                </motion.div>

                {/* Arrow (between steps, not after last) */}
                {index < steps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.15 + 0.1 }}
                    className="hidden md:flex items-center justify-center px-6"
                  >
                    <ArrowRight className="h-8 w-8 text-[#337AF7]/40" strokeWidth={2} />
                  </motion.div>
                )}

                {/* Mobile Arrow (vertical) */}
                {index < steps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.15 + 0.1 }}
                    className="md:hidden py-2"
                  >
                    <ArrowRight className="h-6 w-6 text-[#337AF7]/40 rotate-90" strokeWidth={2} />
                  </motion.div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Bottom Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-12 text-center text-base md:text-lg text-[#162959]/70 max-w-2xl mx-auto italic"
        >
          Let the system do the hard work — checking every improvement point so you can focus on execution.
        </motion.p>
      </div>
    </section>
  );
}
